import { useQuerySearchTemplates } from "@zapier/client";
import { useRouterParams } from "@zapier/shared";
import { useCurrentUser } from "@zapier/client";
import { useEffect, useState } from "react";

interface Input {
  pageSize?: number;
}

export function useTemplatesList(input?: Input) {
  const { search, page, app, owner: selectedOwner, shared } = useRouterParams();
  const { user, isLoading: isUserLoading } = useCurrentUser();

  const shouldFetchTemplates = !isUserLoading && Boolean(user?.id);

  /** Owner filter: same source of truth as Owner column (templates API owner.customUserId).
   * selectedOwner from URL (OwnerFilter), or shared=me / shared=-me from TemplatesSharingFilter. */
  const userId = user?.id ?? "";
  let owner: string | undefined;
  if (selectedOwner) {
    owner = selectedOwner;
  } else if (shared && (isUserLoading || !userId)) {
    owner = undefined;
  } else if (shared === "me") {
    owner = String(userId);
  } else if (shared === "-me") {
    owner = `-${userId}`;
  }

  /** Fetch templates *********************************************** */

  const {
    templates,
    isLoading,
    isFetching,
    page: currentPage,
    totalPages,
    totalDocs,
  } = useQuerySearchTemplates(
    {
      search: search || undefined,
      page: page || undefined,
      limit: input?.pageSize,
      app: app || undefined,
      ...(owner !== undefined && owner !== "" && { owner }),
    },
    { enabled: shouldFetchTemplates },
  );

  /** Loading state  *********************************************************
   * only show loading when filters change */

  const [didFiltersChange, setDidFiltersChange] = useState(true);

  useEffect(() => {
    setDidFiltersChange(true);
  }, [page, search, app, selectedOwner, shared]);

  useEffect(() => {
    if (!isLoading) {
      setDidFiltersChange(false);
    }
  }, [isLoading]);

  return {
    templates: templates || [],
    isLoading: isLoading || isUserLoading, // Set to true when either the templates are loading or the user is loading
    isFetching: isFetching && didFiltersChange,
    page: currentPage ?? 1,
    totalPages,
    totalResults: totalDocs ?? 0,
  };
}
